import React from 'react';
import styled from 'styled-components';
import Typography from '@material-ui/core/Typography';
import { Link } from 'react-router-dom';
import { palette, fonts, brand } from 'theme';
import rgba from 'polished/lib/color/rgba';
import { responsiveText } from 'utilities/style';
import parse, { attributesToProps } from 'html-react-parser';
import loadable from '@loadable/component';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

const ResponsiveInlineImg = loadable(() => import('components/ResponsiveImg'), {
  resolveComponent: components => components.ResponsiveInlineImg
});

const getTextColor = theme => {
  return theme.palette && theme.palette.type === 'dark' ? '#fff' : '#000';
};

const LinkStyle = () => `
  text-decoration:none;
  border-bottom: 2px solid ${rgba(palette.primary.main, 0.35)};
  transition: border-bottom 0.2s linear;
  &:hover {
    border-bottom-color: ${palette.primary.main};
  }
`;

const ContentAnchor = styled.a`
  color: ${props => getTextColor(props.theme)};
  ${LinkStyle};
`;

const ContentLink = styled(Link)`
  color: ${props => getTextColor(props.theme)};
  ${LinkStyle};
`;

const RichTypography = styled(Typography)`
  a {
    color: ${props => getTextColor(props.theme)};

    text-decoration:none;
    border-bottom: 2px solid ${props =>
      rgba(props.anchorOverrideColor || getTextColor(props.theme), 0.35)};

    transition: border-bottom 0.2s linear;
    &:hover {
      border-bottom-color: ${props =>
        props.anchorOverrideColor || getTextColor(props.theme)};
    }
  }

  blockquote {
    border-left: 4px solid ${palette.grey[100]};
    margin-left: 0;
    padding: 20px;
    font-size: 1.1em;
    font-style: italic;
    display: inline-block;

    &.large {
      position: relative;
      font-family:${fonts.headingFamily};
      font-size: 2em;
      font-weight: 400;
      line-height: 1.1em;
      border:none;
      padding:40px;

      &:before {
        content: '';
        position: absolute;
        left: 0;
        width: 4px;
        background: linear-gradient(${brand.main.gradient.primary});
        height: 100%;
      }

      @media (max-width: 640px) {
        padding-left: 20px;
        font-size:1.3em;
      }
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family:${fonts.headingFamily};
    margin-top:1.5em;
    margin-bottom:.75em;
    &:first-child {
      margin-top:0;
    }
  }

  h1 { ${responsiveText(32, 48)}}
  h2 { ${responsiveText(24, 32)}}
  h3 { ${responsiveText(20, 24)}}
  h4 { ${responsiveText(18, 20)}}
  h5 { font-size:1em; line-height:1.1em;}
  h6 { font-size:1em; line-height:1.1em;}

  iframe {
    max-width:100%;
  }

  pre {
    font-family:${fonts.monoFamily};
    background:${palette.grey[50]}
    border-radius:5px;
    padding:20px;
    overflow-x:auto;
  }

  img {
    max-width:100%;
    border-radius:5px;
  }

  hr {
    height: 1px;
    margin: 0;
    border: none;
    flex-shrink: 0;
    background-color: rgba(0, 0, 0, 0.12);
  }

  ul {
    padding-inline-start: 20px;
  }

  li {
    margin-bottom:.5em;
  }

  table {
    border-collapse: collapse;
    width:100%;
    max-width:calc(100vw - 48px);
    overflow-x:auto;
    display:block;
  }

  tr {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  thead tr {
    border-bottom-width:2px;
    font-weight:bold;
  }

  th, td {
    text-align: left;
    padding: 4px;
  }
`;

class ContentTypography extends React.PureComponent {
  render() {
    const { dangerouslySetInnerHTML, children, ...other } = this.props;
    if (dangerouslySetInnerHTML && dangerouslySetInnerHTML.__html) {
      return (
        <RichTypography {...other} component="div">
          {parse(dangerouslySetInnerHTML.__html, {
            replace: ({ name, attribs }) => {
              if (name === 'img') {
                if (attribs && attribs.src.includes('contentstack.io')) {
                  return (
                    <ResponsiveInlineImg {...attributesToProps(attribs)} />
                  );
                } else {
                  return <></>;
                }
              }
              if (name === 'iframe') {
                return (
                  <PrerenderSafeLazyLoad height={attribs.height}>
                    <iframe title="embed" {...attributesToProps(attribs)} />
                  </PrerenderSafeLazyLoad>
                );
              }
            }
          })}
        </RichTypography>
      );
    } else if (children) {
      return (
        <RichTypography {...other} component="div">
          {children}
        </RichTypography>
      );
    } else {
      return null;
    }
  }
}

export default ContentTypography;
export { ContentAnchor, ContentLink };
